import { inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * GST challan tab — inputs for period + challan/CPIN/BSR/CIN, plus a live
 * settlement preview computed by the backend. Split out of
 * ChallanPostDialog to keep component complexity < 50 lines each.
 */
export default function ChallanGstPanel({
    debit, gstPeriod, setGstPeriod,
    challanNo, setChallanNo,
    cpin, setCpin,
    bsrCode, setBsrCode,
    cin, setCin,
    gstPreview, previewBusy, gstCash, gstDiff, gstMatches,
}) {
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
                <FieldBlock label="Return period">
                    <Input type="month" value={gstPeriod} onChange={(e) => setGstPeriod(e.target.value)}
                           className="h-9" data-testid="challan-gst-period" />
                </FieldBlock>
                <FieldBlock label="Challan no. / CPIN">
                    <Input value={challanNo} onChange={(e) => setChallanNo(e.target.value)}
                           className="h-9 font-mono" data-testid="challan-gst-no" />
                </FieldBlock>
                <FieldBlock label="CPIN">
                    <Input value={cpin} onChange={(e) => setCpin(e.target.value)}
                           className="h-9 font-mono" data-testid="challan-gst-cpin" />
                </FieldBlock>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <FieldBlock label="BSR code">
                    <Input value={bsrCode} onChange={(e) => setBsrCode(e.target.value)}
                           className="h-9 font-mono" data-testid="challan-gst-bsr" />
                </FieldBlock>
                <FieldBlock label="CIN">
                    <Input value={cin} onChange={(e) => setCin(e.target.value)}
                           className="h-9 font-mono" data-testid="challan-gst-cin" />
                </FieldBlock>
            </div>
            <SettlementPreview
                busy={previewBusy} preview={gstPreview} period={gstPeriod}
                matches={gstMatches} debit={debit} cash={gstCash} diff={gstDiff}
            />
            <div className="text-[11px] text-stone-500">
                On post: Debit every Output CGST/SGST/IGST for {gstPeriod}, credit Input GST utilised
                (per standard set-off rules), and credit Bank ₹{inr(debit)}. ITC carry-forward is preserved.
            </div>
        </div>
    );
}


function SettlementPreview({ busy, preview, period, matches, debit, cash, diff }) {
    if (busy) return <div className="text-stone-500 text-sm text-center py-4">Computing settlement…</div>;
    if (!preview) return null;
    return (
        <div className="border rounded-md" style={{ borderColor: "var(--se-border)" }}
             data-testid="challan-gst-preview">
            <div className="px-3 py-2 border-b flex items-center justify-between text-sm"
                 style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                <span className="font-medium">Settlement preview for {period}</span>
                <span className={matches ? "text-emerald-700 text-xs font-medium" : "text-amber-700 text-xs font-medium"}>
                    {matches ? (
                        <><CheckCircle size={12} className="inline mr-1" /> Matches bank</>
                    ) : (
                        <><Warning size={12} className="inline mr-1" /> Bank ₹{inr(debit)} vs Computed ₹{inr(cash)} — Δ ₹{inr(diff)}</>
                    )}
                </span>
            </div>
            <div className="p-3 text-sm grid grid-cols-3 gap-x-4 gap-y-1">
                <ColHeader>Head</ColHeader>
                <ColHeader right>Liability</ColHeader>
                <ColHeader right>Cash</ColHeader>
                <HeadRow head="CGST" liab={preview.outputs.cgst} cash={preview.cash_to_pay.cgst} />
                <HeadRow head="SGST" liab={preview.outputs.sgst} cash={preview.cash_to_pay.sgst} />
                <HeadRow head="IGST" liab={preview.outputs.igst} cash={preview.cash_to_pay.igst} />
                <div className="font-medium border-t pt-1" style={{ borderColor: "var(--se-border)" }}>Total cash</div>
                <div className="border-t" style={{ borderColor: "var(--se-border)" }}></div>
                <div className="se-num text-right font-medium border-t pt-1"
                     style={{ borderColor: "var(--se-border)", color: "#9f1239" }}>₹ {inr(cash)}</div>
            </div>
        </div>
    );
}


function ColHeader({ children, right }) {
    return (
        <div className={`text-stone-500 text-xs uppercase tracking-widest ${right ? "text-right" : ""}`}>
            {children}
        </div>
    );
}

function HeadRow({ head, liab, cash }) {
    return (
        <>
            <div>{head}</div>
            <div className="se-num text-right">₹ {inr(liab)}</div>
            <div className="se-num text-right" style={{ color: "#9f1239" }}>₹ {inr(cash)}</div>
        </>
    );
}

function FieldBlock({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
            {children}
        </div>
    );
}
